/**
 * Fixed red banner that renders on every portal page while the current
 * session is an admin impersonation. Polls once on mount — no ongoing cost.
 *
 * Renders nothing when the session is a real customer session.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye } from 'lucide-react';

import { adminApi } from '@/lib/api';

export default function ImpersonationBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [target, setTarget] = useState(null);
  const [returning, setReturning] = useState(false);

  // Re-check impersonation state on every client-side route change so the
  // banner appears immediately after an admin clicks "View As" on
  // /admin/customers (which navigates to /portal/dashboard via React Router
  // without a page reload). We only bother checking on portal routes — no
  // point pinging the API on /admin, /verify, or the landing page.
  useEffect(() => {
    if (!location.pathname.startsWith('/portal')) {
      setTarget(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.heartbeat();
        if (cancelled) return;
        if (data?.impersonated) {
          setTarget(data.target || 'customer');
        } else {
          setTarget(null);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Ends the impersonation session (revokes the customer cookie) but leaves
  // the admin cookie intact, then routes back to the admin customer list.
  const backToAdmin = useCallback(async () => {
    if (returning) return;
    setReturning(true);
    try {
      await adminApi.exit();
    } catch {
      /* ignore */
    }
    navigate('/admin/customers', { replace: true });
    window.location.reload();
  }, [returning, navigate]);

  if (!target) return null;

  return (
    <div
      className="sticky top-0 z-40 bg-red-600 text-white text-xs sm:text-sm font-semibold px-4 py-2 flex items-center justify-center gap-3 shadow"
      data-testid="impersonation-banner"
    >
      <Eye className="h-4 w-4 shrink-0" />
      <span className="truncate">
        Viewing as <span className="font-bold">{target}</span> — no
        notifications will be sent
      </span>
      <button
        type="button"
        onClick={backToAdmin}
        disabled={returning}
        className="ml-2 inline-flex items-center gap-1 rounded-full bg-white/20 hover:bg-white/30 px-3 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
        data-testid="impersonation-banner-back-to-admin"
      >
        <ArrowLeft className="h-3 w-3" />
        {returning ? 'Returning…' : 'Back to Admin'}
      </button>
    </div>
  );
}
